import { FC, useState } from 'react';
import { toast } from 'react-toastify';

import { isApiSuccess } from '@/api/shared.types.ts';
import {
  updateVoucherValidity,
  createComplimentaryVoucherBatch,
} from '@/api/voucher/voucher.actions.ts';
import {
  VoucherSummary,
  CreateComplimentaryVoucherFormData,
} from '@/api/voucher/voucher.types.ts';
import FullScreenMediaViewer from '@/components/controls/FullScreenMediaViewer.tsx';
import Pagination from '@/components/layout/Pagination.tsx';
import ComplimentaryVoucherModal from '@/components/layout/vouchers/ComplimentaryVoucherModal.tsx';
import VoucherDetailsModal from '@/components/layout/vouchers/VoucherDetailsModal.tsx';
import VoucherHierarchicalTable from '@/components/layout/vouchers/VoucherHierarchicalTable.tsx';
import { useAsync } from '@/hooks/global/useAsync.ts';
import { useVouchers } from '@/hooks/useVouchers.ts';
import { useVoucherSelection } from '@/hooks/useVoucherSelection.ts';
import { useAuthStore, UserRoles } from '@/stores/useAuthStore.ts';
import { generateVouchersBatchPdf } from '@/utils/generateVoucherPdf.ts';

const VouchersDashboard: FC = () => {
  const {
    voucherBatchesCount,
    voucherBatches,
    refetch,
    voucherDetails,
    setVoucherDetails,
    fetchVoucherDetails,
    isFetchingAll,
    pageSize,
    currentPage,
    setCurrentPage,
    searchText,
    setSearchText,
  } = useVouchers();

  const {
    selectedVouchers,
    expandedBatches,
    files,
    selectedIndex,
    isGeneratingPdf,
    toggleBatch,
    selectBatch,
    selectVoucher,
    generatePdf,
    showPdf,
    closePdf,
    onNext,
    onPrev,
  } = useVoucherSelection(voucherBatches);

  const [showViewModal, setShowViewModal] = useState(false);
  const [showComplimentaryModal, setShowComplimentaryModal] = useState(false);
  const { role, isAdmin } = useAuthStore();
  const isUserCorporate = role === UserRoles.CORPORATE;

  const { run: runUpdate, isLoading: isUpdating } = useAsync();
  const { run: runCreateComplimentary, isLoading: isCreatingComplimentary } =
    useAsync();

  const handleView = async (voucher: VoucherSummary): Promise<void> => {
    await fetchVoucherDetails(voucher.id);
    setShowViewModal(true);
  };

  const handleUpdateVoucherValidity = async (
    voucherBatchId: string,
    validFor: string
  ): Promise<void> => {
    const response = await runUpdate(() =>
      updateVoucherValidity(voucherBatchId, validFor)
    );

    if (!isApiSuccess(response)) {
      toast.error(response.error.message || 'Failed to update voucher date');
      return;
    }

    toast.success('Voucher date updated successfully');

    if (voucherDetails) {
      await fetchVoucherDetails(voucherDetails.id);
    }

    await refetch(currentPage);
  };

  const handleCreateComplimentarySubmit = async (
    data: CreateComplimentaryVoucherFormData
  ): Promise<void> => {
    const response = await runCreateComplimentary(() =>
      createComplimentaryVoucherBatch(data)
    );

    if (!isApiSuccess(response)) {
      toast.error(response.error.message || 'Failed to create vouchers');
      return;
    }

    toast.success(
      `Complimentary ${data.voucherCount === 1 ? 'voucher' : 'vouchers'} created successfully`
    );

    if (response.content.vouchers.length > 0) {
      const pdfBlob = await generateVouchersBatchPdf(response.content.vouchers);
      showPdf(pdfBlob, 'complimentary-vouchers.pdf');
    }

    await refetch(currentPage);
  };

  return (
    <>
      <VoucherHierarchicalTable
        batches={voucherBatches}
        expandedBatches={expandedBatches}
        selectedVouchers={selectedVouchers}
        isGeneratingPdf={isGeneratingPdf}
        isLoading={isFetchingAll}
        isUserCorporate={isUserCorporate}
        onToggleBatch={toggleBatch}
        onSelectBatch={selectBatch}
        onSelectVoucher={selectVoucher}
        onView={handleView}
        onGeneratePdf={selectedVouchers.length > 0 ? generatePdf : null}
        searchText={searchText}
        setSearchText={setSearchText}
        voucherBatchesCount={voucherBatchesCount}
        onAddComplimentaryVoucher={
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          isAdmin ? () => setShowComplimentaryModal(true) : undefined
        }
      />

      <VoucherDetailsModal
        isOpen={showViewModal}
        onClose={() => {
          setVoucherDetails(null);
          setShowViewModal(false);
        }}
        details={voucherDetails}
        hasActionRole={isUserCorporate}
        isAdmin={isAdmin}
        onUpdate={handleUpdateVoucherValidity}
        isUpdating={isUpdating}
      />

      <ComplimentaryVoucherModal
        isOpen={showComplimentaryModal}
        onClose={() => setShowComplimentaryModal(false)}
        onSubmit={handleCreateComplimentarySubmit}
        isLoading={isCreatingComplimentary}
      />

      <FullScreenMediaViewer
        files={files}
        currentIndex={selectedIndex}
        onClose={closePdf}
        onNext={onNext}
        onPrev={onPrev}
        isOpen={selectedIndex !== null}
      />

      <Pagination
        totalItems={voucherBatchesCount}
        pageSize={pageSize}
        currentPage={currentPage}
        goToPage={setCurrentPage}
      />
    </>
  );
};

export default VouchersDashboard;
